import { ReactElement, useState } from 'react'
import { Resource, ResourceProps } from 'react-admin'
import { useParams } from 'react-router-dom'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material'

import { parseDiscussionCommentIdentityKey } from './chapterDiscussions.model'
import {
  DiscussionModerationList,
  type DiscussionModerationListProps,
} from './DiscussionModerationList'
import {
  DiscussionModerationShow,
  type DiscussionModerationShowProps,
} from './DiscussionModerationShow'
import {
  DiscussionReportList,
  type DiscussionReportListProps,
} from './DiscussionReportList'
import {
  DiscussionUserEnforcementPanel,
  type DiscussionUserEnforcementPanelProps,
} from './DiscussionUserEnforcementPanel'
import {
  DiscussionBulkDeleteJobPanel,
  type DiscussionBulkDeleteJobPanelProps,
} from './DiscussionBulkDeleteJobPanel'

export type AdminResourceDefinition = ReactElement<ResourceProps>

function DiscussionDashboard() {
  const [tab, setTab] = useState<'moderation' | 'reports'>('moderation')
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5">Chapter discussions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review AI decisions, reports, evidence, and user enforcement.
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, value: 'moderation' | 'reports') => setTab(value)}
        sx={{ mb: 2 }}>
        <Tab value="moderation" label="Moderation" />
        <Tab value="reports" label="Reports" />
      </Tabs>
      {tab === 'moderation' ? (
        <DiscussionModerationList />
      ) : (
        <DiscussionReportList />
      )}
    </Box>
  )
}

function DiscussionModerationShowRoute() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Alert severity="error">Missing comment identity.</Alert>
  try {
    return (
      <DiscussionModerationShow
        identity={parseDiscussionCommentIdentityKey(id)}
      />
    )
  } catch (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Invalid comment identity'}
      </Alert>
    )
  }
}

export const discussionCsResources: AdminResourceDefinition[] = [
  <Resource
    key="chapter-discussions"
    name="chapter-discussions"
    options={{ label: 'Chapter Discussions' }}
    icon={ForumOutlinedIcon}
    list={DiscussionDashboard}
    show={DiscussionModerationShowRoute}
  />,
]

export {
  DiscussionBulkDeleteJobPanel,
  DiscussionModerationList,
  DiscussionModerationShow,
  DiscussionReportList,
  DiscussionUserEnforcementPanel,
}

export type {
  DiscussionBulkDeleteJobPanelProps,
  DiscussionModerationListProps,
  DiscussionModerationShowProps,
  DiscussionReportListProps,
  DiscussionUserEnforcementPanelProps,
}

export * from './chapterDiscussions.types'
